import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, UserRound } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input, Select, statusLabel } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { minorFromInput } from '@/components/MoneyInput';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';

export function PreAuthExtendPage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('preauth'));
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [patientId, setPatientId] = useState(params.get('patientId') ?? '');
  const [needed, setNeeded] = useState(params.get('admissionId') ?? '');
  const [authTo, setAuthTo] = useState('');
  const [amount, setAmount] = useState('');
  const [procedure, setProcedure] = useState('');
  const [error, setError] = useState<string | null>(null);

  const admitted = useQuery({
    queryKey: ['patient-admissions', patientId],
    queryFn: () => get<{ id: string; number: string; status: string }[]>(`/insurance/patients/${patientId}/admissions`),
    enabled: Boolean(patientId),
  });

  const extend = useMutation({
    mutationFn: () =>
      post<{ id: string }>('/insurance/pre-auths/extend', {
        admissionId: needed,
        authTo: new Date(`${authTo}T23:59:59.000Z`).toISOString(),
        requestedAmountMinor: amount ? minorFromInput(amount) : 0,
        ...(procedure.trim() ? { procedure: procedure.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success('Extension raised', 'Send it to the insurer from the Pending tab');
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-counts'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(needed && authTo);
  const dirty = !extend.isSuccess && Boolean(patientId || needed || authTo || amount || procedure);

  return (
    <FormScreen
      title="Extend the stay"
      description="Starts the day after the last approved window ends. Windows that overlap another request are refused."
      icon={CalendarPlus}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        extend.mutate();
      }}
    >
      <FormSection title={tr('billing', 'patient')} icon={UserRound}>
        <Field label="Patient" required>
          <PatientPicker
            value={patientId}
            onChange={(id) => {
              setPatientId(id);
              setNeeded('');
            }}
          />
        </Field>
        <Field label="Admission" required>
          <Select value={needed} onChange={(event) => setNeeded(event.target.value)} disabled={!patientId}>
            <option value="">Choose…</option>
            {admitted.data?.map((admission) => (
              <option key={admission.id} value={admission.id}>
                {admission.number} · {statusLabel(admission.status, 'admission_status')}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>
      <FormSection title={tr('billing', 'extension')} icon={CalendarPlus}>
        <Field label="Extended until" required>
          <DatePicker value={authTo} onChange={setAuthTo} />
        </Field>
        <Field label="Amount asked for">
          <Input type="text" dir="ltr" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </Field>
        <Field label="Service" hint="Billing code and description; taken from the approved request when left empty" className="sm:col-span-2">
          <Input value={procedure} onChange={(event) => setProcedure(event.target.value)} />
        </Field>
      </FormSection>
      {error && <Alert variant="destructive">{error}</Alert>}
      <FormActions submitLabel="Raise the extension" loading={extend.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
